import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Grid, Button } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { NkSelect, _NkInput as NkInput } from '@nordkap/nordkap-ui';
import { combineClasses } from '../../../utils/styling';
import { useStyles } from './styles';
import api from '../../../infrastructure/api/api';
import appInsights from '../../../infrastructure/appInsights';
import { AccountingObjects } from './accountingObjects';
import { generalSettingsSize } from '../../../components/templateEditor/utils/ruleSizing';

const AdminGeneral = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const languages = useMemo(
    () => [
      { value: 'sv', title: t('language.sv') },
      { value: 'en', title: t('language.en') },
      { value: 'nb', title: t('language.nb') },
    ],
    [t]
  );

  const certificateHandlings = useMemo(
    () => [
      {
        value: 'Prepaid',
        title: t('adminPage.certificateHandlingType.prepaid'),
      },
      {
        value: 'PrepaidWithAccrued',
        title: t('adminPage.certificateHandlingType.prepaidWithAccrued'),
      },
      {
        value: 'Postpaid',
        title: t('adminPage.certificateHandlingType.postpaid'),
      },
    ],
    [t]
  );

  const [certificateHandling, setCertificateHandling] = useState('Prepaid');
  const [gl07Interface, setGl07Interface] = useState('NA');
  const [gl07VoucherType, setGl07VoucherType] = useState('NA');
  const [xifLogType, setXifLogType] = useState('');
  const [language, setLanguage] = useState('sv');
  const [accountingObjects, setAccountingObjects] = useState([]);

  useEffect(() => {
    appInsights.trackPageView();

    api.get('generalsettings', enqueueSnackbar, t).then(generalSettings => {
      if (generalSettings !== null) {
        setCertificateHandling(
          generalSettings.certificateHandling || 'Prepaid'
        );
        setGl07Interface(generalSettings.gl07Interface || 'NA');
        setGl07VoucherType(generalSettings.gl07VoucherType || 'NA');
        setXifLogType(generalSettings.xifLogType || '');
        setLanguage(generalSettings.language || 'sv');
        setAccountingObjects(
          generalSettings.accountingObjects.sort((a, b) => a.index - b.index)
        );
      }
    });
  }, [t, enqueueSnackbar]);

  const createAdminGeneralSettings = () => ({
    certificateHandling,
    gl07Interface,
    gl07VoucherType,
    xifLogType,
    language,
    accountingObjects: accountingObjects
      .map(ao => ({ ...ao, name: ao.name ? ao.name.trim() : ao.name }))
      .sort((a, b) => a.index - b.index),
  });

  const saveAdminGeneral = () => {
    const adminGeneralSettings = createAdminGeneralSettings();
    api.post('GeneralSettings', adminGeneralSettings, enqueueSnackbar, t);
  };

  const changeAccountingObject = useCallback(
    event => {
      const newObjects = [...accountingObjects];
      const index = event.target.name.split('-')[0];
      const object = newObjects.find(o => o.index.toString() === index);
      const property = event.target.name.split('-')[1];
      object[property] = event.target.value;

      setAccountingObjects(newObjects);
    },
    [accountingObjects]
  );

  return (
    <div className={classes.Container}>
      <Grid container xs={12}>
        <Grid item {...generalSettingsSize}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="language"
            >
              {t('adminPage.language')}
            </label>
            <NkSelect
              variant="outlined"
              name="language"
              itemPropName="value"
              itemTitlePropName="title"
              items={languages}
              value={language}
              onChange={event => setLanguage(event.target.value)}
            />
          </div>
        </Grid>
        <Grid item {...generalSettingsSize}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="certificateHandling"
            >
              {t('adminPage.certificateHandling')}
            </label>
            <NkSelect
              variant="outlined"
              name="certificateHandling"
              itemPropName="value"
              itemTitlePropName="title"
              items={certificateHandlings}
              value={certificateHandling}
              onChange={event => setCertificateHandling(event.target.value)}
            />
          </div>
        </Grid>
        <Grid item {...generalSettingsSize}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="gl07Interface"
            >
              {t('adminPage.gl07Interface')}
            </label>
            <NkInput
              name="gl07Interface"
              type="text"
              variant="outlined"
              value={gl07Interface}
              placeholder={t('adminPage.gl07Interface')}
              onChange={event => setGl07Interface(event.target.value)}
            />
          </div>
        </Grid>
        <Grid item {...generalSettingsSize}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="gl07VoucherType"
            >
              {t('adminPage.gl07VoucherType')}
            </label>
            <NkInput
              name="gl07VoucherType"
              type="text"
              variant="outlined"
              value={gl07VoucherType}
              placeholder={t('adminPage.gl07VoucherType')}
              onChange={event => setGl07VoucherType(event.target.value)}
            />
          </div>
        </Grid>
        <Grid item {...generalSettingsSize}>
          <div className={classes.Field}>
            <label
              className={combineClasses([classes.Bold, classes.UpperCase])}
              htmlFor="xifLogType"
            >
              {t('adminPage.xifLogType')}
            </label>
            <NkInput
              name="xifLogType"
              type="text"
              variant="outlined"
              value={xifLogType}
              placeholder={t('adminPage.xifLogType')}
              onChange={event => setXifLogType(event.target.value)}
            />
          </div>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <label
          className={combineClasses([
            classes.Bold,
            classes.UpperCase,
            classes.tableHeaderLabel,
          ])}
        >
          {t('fieldsAndTemplatesPage.accountingObjects')}
        </label>
      </Grid>
      <Grid
        item
        container
        justifyContent="center"
        xs={12}
        spacing={1}
        className={classes.tableWrapper}
      >
        <AccountingObjects
          accountingObjects={accountingObjects}
          changeObject={changeAccountingObject}
        />
      </Grid>
      <Grid item xs={4} />

      <Grid item container xs={12} justifyContent="flex-end">
        <Button
          id="saveAdminGeneral"
          variant="contained"
          color="primary"
          title={t('global.buttons.saveAll')}
          onClick={saveAdminGeneral}
          className={classes.SaveAllButton}
          disabled={accountingObjects.length !== 8}
        >
          {t('global.buttons.saveAll')}
        </Button>
      </Grid>
      <Grid item xs={4} />
    </div>
  );
};

export default AdminGeneral;
